import type { DamageCalc, SummaryTotals } from "@/lib/calc";
import type { AreaFormRow, FacilityFormRow, InsuranceFormData, UnitFormRow } from "../components/insurance/form/types";

function sanitizeNum(v: number | null | undefined): number | null {
  if (v == null || !Number.isFinite(v)) return null;
  return v;
}

function sanitizeDate(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function buildAreaPayload(rows: AreaFormRow[]): Record<string, unknown>[] {
  return rows.map((a) => ({
    description: a.description || null,
    eyrBuilt: sanitizeNum(a.eyrBuilt),
    area: sanitizeNum(a.area),
    multiple: sanitizeNum(a.multiple),
    rate: a.rateOverride ? sanitizeNum(a.rate) : null,
    addGst: a.addGst,
    firstYearMonths: sanitizeNum(a.firstYearMonths),
    demoPlanMonths: sanitizeNum(a.demoPlanMonths),
    constructionMonths: sanitizeNum(a.constructionMonths),
    percentagePa: sanitizeNum(a.percentagePa),
    inflationYear1Pct: sanitizeNum(a.inflationYear1Pct),
    inflationYear2Pct: sanitizeNum(a.inflationYear2Pct),
    inflationYear3Pct: sanitizeNum(a.inflationYear3Pct),
    inflationYear4Pct: sanitizeNum(a.inflationYear4Pct),
    demolitionPercentage: sanitizeNum(a.demolitionPercentage),
    calcReinstatementExcl: sanitizeNum(a.calcReinstatementExcl),
    calcReinstatementIncl: sanitizeNum(a.calcReinstatementIncl),
    calcReinstatementMonths: sanitizeNum(a.calcReinstatementMonths),
    calcInflationTotal: sanitizeNum(a.calcInflationTotal),
    calcDemolitionCost: sanitizeNum(a.calcDemolitionCost),
    calcDepreciation: sanitizeNum(a.calcDepreciation),
    calcDrcForFsl: sanitizeNum(a.calcDrcForFsl),
  }));
}

function buildFacilityPayload(rows: FacilityFormRow[]): Record<string, unknown>[] {
  return rows.map((f) => ({
    category: f.category,
    description: f.description || null,
    area: sanitizeNum(f.area),
    multiple: sanitizeNum(f.multiple),
    rate: f.rateOverride ? sanitizeNum(f.rate) : null,
    addGst: f.addGst,
    inflationYear1Pct: sanitizeNum(f.inflationYear1Pct),
    inflationYear2Pct: sanitizeNum(f.inflationYear2Pct),
    inflationYear3Pct: sanitizeNum(f.inflationYear3Pct),
    inflationYear4Pct: sanitizeNum(f.inflationYear4Pct),
    demolitionPercentage: sanitizeNum(f.demolitionPercentage),
    calcReinstatementIncl: sanitizeNum(f.calcReinstatementIncl),
    calcInflationTotal: sanitizeNum(f.calcInflationTotal),
    calcDemolitionCost: sanitizeNum(f.calcDemolitionCost),
  }));
}

function buildUnitPayload(rows: UnitFormRow[]): Record<string, unknown>[] {
  return rows.map((u) => ({
    label: u.label,
    use: u.use || "Residential",
    configuration: u.configuration || null,
    grossFloorArea: sanitizeNum(u.grossFloorArea),
    includedInEstimate: u.includedInEstimate,
  }));
}

function buildDamagePayload(
  formValues: InsuranceFormData,
  damageCalc: DamageCalc | null,
): Record<string, unknown> | null {
  if (!formValues.damageAmount) return null;
  return {
    damageAmount: sanitizeNum(formValues.damageAmount),
    damageChangePercentage: sanitizeNum(formValues.damageChangePercentage),
    calcAdjustedDamage: sanitizeNum(damageCalc?.adjustedDamage),
    calcInflationTotal: sanitizeNum(damageCalc?.reinstatementInflationTotal),
    firstYearMonths: sanitizeNum(formValues.damageFirstYearMonths),
    demoPlanMonths: sanitizeNum(formValues.damageDemoPlanMonths),
    constructionMonths: sanitizeNum(formValues.damageConstructionMonths),
    inflationYear1Pct: sanitizeNum(formValues.damageInflationYear1Pct),
    inflationYear2Pct: sanitizeNum(formValues.damageInflationYear2Pct),
    inflationYear3Pct: sanitizeNum(formValues.damageInflationYear3Pct),
    inflationYear4Pct: sanitizeNum(formValues.damageInflationYear4Pct),
  };
}

interface BuildInsurancePayloadArgs {
  formValues: InsuranceFormData;
  imagesIntro: string;
  areas: AreaFormRow[];
  units: UnitFormRow[];
  facilities: FacilityFormRow[];
  damageCalc: DamageCalc | null;
  summary: SummaryTotals;
  roundingAdjustments: { reinstatement: number; inflation: number; demolition: number; drc: number };
}

/**
 * Assembles the API payload for creating/updating an insurance record from
 * the form's live state: raw inputs plus the calculated outputs the models
 * store (reinstatement/inflation/demolition/DRC totals and per-row calcs).
 */
export function buildInsurancePayload(args: BuildInsurancePayloadArgs): Record<string, unknown> {
  const { formValues, imagesIntro, areas, units, facilities, damageCalc, summary, roundingAdjustments } = args;

  return {
    insured: formValues.insured || undefined,
    contactFullName: formValues.contactFullName || null,
    contactFirstName: formValues.contactFirstName || null,
    contactAddress1: formValues.contactAddress1 || null,
    contactAddress2: formValues.contactAddress2 || null,
    contactPhone: formValues.contactPhone || null,
    contactEmail: formValues.contactEmail || null,
    address: formValues.address || undefined,
    latitude: sanitizeNum(formValues.latitude),
    longitude: sanitizeNum(formValues.longitude),
    buildingName: formValues.buildingName || null,
    numberOfUnits: formValues.numberOfUnits || 1,

    propertyTypeId: formValues.propertyTypeId ? Number(formValues.propertyTypeId) : null,
    useOcc: formValues.useOcc || null,
    yearBuilt: formValues.yearBuilt != null ? String(formValues.yearBuilt) : null,
    eyrBuilt: sanitizeNum(formValues.eyrBuilt),
    land: formValues.land || null,
    subsoilId: formValues.subsoilId ? Number(formValues.subsoilId) : null,
    upgradeRequirements: formValues.upgradeRequirements || null,
    heritageListing: formValues.heritageListing || null,
    seismicGrade: formValues.seismicGrade || null,
    pocFrom: sanitizeDate(formValues.pocFrom),
    pocTo: sanitizeDate(formValues.pocTo),
    certificateDate: sanitizeDate(formValues.certificateDate),
    reinstatementMonths: sanitizeNum(formValues.firstYearMonths),
    valuerId: formValues.valuerPersonId ? Number(formValues.valuerPersonId) : null,
    feeAmountExGst: sanitizeNum(formValues.feeAmountExGst),
    accountNumberForXero: formValues.accountNumberForXero || null,
    propertyCategory: formValues.propertyCategory || null,

    assetDescription: formValues.assetDescription || null,
    construction: formValues.construction || null,
    recreationalFeatures: formValues.recreationalFeatures || null,
    retainingWalls: formValues.retainingWalls || null,
    specialFeatures: formValues.specialFeatures || null,
    features: formValues.features || null,
    detailedDescription: formValues.detailedDescription || null,
    imagesIntro: imagesIntro || null,

    sModal: sanitizeNum(formValues.sModal),
    eModal: sanitizeNum(formValues.eModal),
    percentChange: sanitizeNum(formValues.percentChange),
    age: sanitizeNum(formValues.age),
    feesPercentage: sanitizeNum(formValues.feesPercentage),
    demolitionPercentage: sanitizeNum(formValues.demolitionPercentage),
    gst: formValues.gst || 15,
    inflationDemoPlanMonths: sanitizeNum(formValues.inflationDemoPlanMonths),
    inflationConstructionMonths: sanitizeNum(formValues.inflationConstructionMonths),
    inflationYear1: sanitizeNum(formValues.inflationYear1),
    inflationYear2: sanitizeNum(formValues.inflationYear2),
    inflationYear3: sanitizeNum(formValues.inflationYear3),
    inflationYear4: sanitizeNum(formValues.inflationYear4),

    damageAmount: sanitizeNum(formValues.damageAmount),
    damageChangePercentage: sanitizeNum(formValues.damageChangePercentage),

    fslText: formValues.fslText || null,
    note: formValues.note || null,

    excludeFromMailout: formValues.excludeFromMailout,
    isActive: formValues.isActive,
    owner: formValues.owner || null,
    ownerAddress: formValues.ownerAddress || null,
    broker: formValues.broker || null,
    brokerAddress: formValues.brokerAddress || null,
    instructingParty: formValues.instructingParty || null,
    instructingEmail: formValues.instructingEmail || null,
    invoiceTo: formValues.invoiceTo || null,

    // Stored calculated summary totals
    reinstatementEstimate: sanitizeNum(summary.reinstatementEstimate),
    reinstatementInflation: sanitizeNum(summary.reinstatementInflation),
    totalDemolition: sanitizeNum(summary.totalDemolition),
    drcForFsl: sanitizeNum(summary.drcForFsl),
    reinstatementCostEstimatePerUnit: sanitizeNum(summary.reinstatementPerUnit),
    roundingReinstatement: roundingAdjustments.reinstatement,
    roundingInflation: roundingAdjustments.inflation,
    roundingDemolition: roundingAdjustments.demolition,
    roundingDrc: roundingAdjustments.drc,

    areas: buildAreaPayload(areas),
    units: buildUnitPayload(units),
    facilities: buildFacilityPayload(facilities),
    damage: buildDamagePayload(formValues, damageCalc),
  };
}
